import { useCallback, useEffect, useState } from 'react';
import useSystemLoading from 'utils/hooks/useSystemLoading';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  JobFamilyModel,
  JobFamiliesModel,
  MutationCreateJobFamilyArgs,
  MutationPatchJobFamilyArgs,
  JobFamilyCreateModelInput,
  JobFamilyPatchModelInput,
  JobFamilyQueryModelInput,
  PageModelInput
} from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { CREATE_JOB_FAMILY, GET_JOB_FAMILIES, PATCH_JOB_FAMILY, REMOVE_JOB_FAMILY } from './jobFamily.graphql';
import { useJobFamiliesIntl } from './translations/useJobFamiliesIntl';

export type JobFamilyQuery = DataTableTypes.OnVariablesChanged<{ search: string }, 'name'>;

export function useJobFamilies() {
  const intl = useJobFamiliesIntl();
  const { showSuccess, showError } = useSnackbar();

  const [variables, setVariables] = useState<JobFamilyQuery | undefined>({
    page: { offset: 0, limit: 40 },
    query: { search: '' }
  });

  const [getFamilies, { data: jobFamilies, loading }] = useLazyQuery<
    { jobFamilies: JobFamiliesModel },
    DeepPartial<{ query: JobFamilyQueryModelInput; page: PageModelInput }>
  >(GET_JOB_FAMILIES);

  useEffect(() => {
    getFamilies({ variables: { query: variables?.query, page: variables?.page } });
  }, [getFamilies, variables?.page, variables?.query]);

  const [createJobFamily, { loading: createLoading }] = useMutation<
    { jobFamily: JobFamilyModel },
    { mutation?: DeepPartial<JobFamilyCreateModelInput> }
  >(CREATE_JOB_FAMILY, {
    onError: () => showError(intl.failedToCreateJobFamily)
  });

  const [patchJobFamily, { loading: patchLoading }] = useMutation<
    { jobFamily: JobFamilyModel },
    {
      jobFamilyUid?: string;
      jobFamily?: DeepPartial<JobFamilyPatchModelInput>;
    }
  >(PATCH_JOB_FAMILY, {
    onError: () => showError(intl.failedToUpdateJobFamily)
  });

  const [removeJobFamily, { loading: removeLoading }] = useMutation<
    { deleteJobFamily: { uid: string } },
    { uid: string }
  >(REMOVE_JOB_FAMILY, {
    onError: (e) => showError(e.message)
  });

  useSystemLoading(patchLoading || createLoading || removeLoading || loading);

  const create = useCallback(
    async (params: DeepPartial<MutationCreateJobFamilyArgs>) => {
      const created = await createJobFamily({
        variables: structuredClone(params)
      });
      showSuccess(intl.newJobFamilyCreatedSuccessfully);
      return created.data?.jobFamily;
    },
    [createJobFamily, intl.newJobFamilyCreatedSuccessfully, showSuccess]
  );

  const patch = useCallback(
    async (params: DeepPartial<MutationPatchJobFamilyArgs>) => {
      const patched = await patchJobFamily({
        variables: structuredClone(params)
      });
      showSuccess(intl.jobProfileUpdatedSuccessfully);
      return patched.data?.jobFamily;
    },
    [intl.jobProfileUpdatedSuccessfully, patchJobFamily, showSuccess]
  );

  const remove = useCallback(
    async (uids: string[]) => {
      await Promise.all(uids.map((uid) => removeJobFamily({ variables: { uid } })));
      showSuccess(intl.jobFamilyDeletedSuccessfully);
    },
    [intl.jobFamilyDeletedSuccessfully, removeJobFamily, showSuccess]
  );

  return {
    create,
    patch,
    remove,
    getFamilies,
    loading,
    totalCount: jobFamilies?.jobFamilies?.totalCount || 0,
    jobFamilies: jobFamilies?.jobFamilies?.items || [],
    variables,
    setVariables
  };
}
